import { useGetGraphqlDataQuery } from '@repo/services';
import { DropdownRenderTypeConfig, useDebounce } from '@repo/utils';
import DataTypeSpecificInput from 'advanced-filters/components/DataTypeSpecificInput';
import { type ComparatorType } from 'advanced-filters/data/comparators';
import { ALL_DATA_TYPES, DATA_TYPES_WITH_OPTIONS } from 'advanced-filters/data/dataTypes';
import {
  type BaseOptionsType,
  FILTERABLE_ENTITY_TYPES,
  type FilterableEntityTypes,
} from 'advanced-filters/data/options';
import { listTaskTemplates } from 'advanced-filters/queries';
import { getAllActivities } from 'advanced-filters/queries/leadActivities';
import { getLeadAttributeOptions } from 'advanced-filters/queries/leadAttributeOptions';
import { getAllSalesGroups } from 'advanced-filters/queries/salesGroups';
import { type KeyValueType, setFetchError } from 'advanced-filters/store/filter';
import { Select } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

const DOCUMENT_MAP : Record<FilterableEntityTypes, {
  document: string,
  object: string,
  requireVariables?: boolean,
  remoteSearchable?: boolean,
}> = {
  lead_activity: {
    document: getAllActivities,
    object: 'activities',
  },
  sales_group: {
    document: getAllSalesGroups,
    object: 'salesGroups',
  },
  lead_attributes: {
    document: getLeadAttributeOptions,
    object: 'leadAttributeOptions',
    requireVariables: true,
    remoteSearchable: true,
  },
  task_type: {
    document: listTaskTemplates,
    object: 'taskTemplates',
  },
  activity_attributes: {
    document: '',
    object: '',
    requireVariables: undefined,
    remoteSearchable: undefined,
  },
};

/**
 * Select statement which Queries options based on the entity
 * and renders the appropriate input based on the data type
 */
export default function SelectWithQuery({
  context,
  value,
  entity,
  handleChange,
  comparisionType,
}: {
  context: 'condition' | 'subCondition';
  value: any;
  entity: BaseOptionsType;
  comparisionType?: ComparatorType | undefined;
  handleChange: (value: KeyValueType) => void;
}) {
  const [dropdownSearchString, setDropdownSearchString] = useState('');
  const debouncedSearchString = useDebounce(dropdownSearchString);

  const dispatch = useDispatch();
  const {
    document,
    requireVariables,
    object,
    remoteSearchable: entitySearchable,
  } = DOCUMENT_MAP[entity.entityType];

  const remoteSearchable = entitySearchable
    && entity?.dataTypeConfig
    && entity?.dataTypeConfig?.dropdownRenderType === DropdownRenderTypeConfig.REMOTE_SEARCHABLE;

  const variables = useMemo(() => {
    if (requireVariables) {
      return {
        id: parseInt(entity?.value, 10),
        searchString: remoteSearchable ? debouncedSearchString : undefined,
        includeIds: (entity?.dataType === ALL_DATA_TYPES.dropdown && value?.value?.length) ? value
          ?.value?.map((val: any) => val.value) : null,
      };
    }
    return {};
  }, [requireVariables, entity?.value, entity?.dataType, remoteSearchable, debouncedSearchString, value?.value]);

  const {
    data,
    isLoading,
    error,
  } = useGetGraphqlDataQuery({
    document,
    variables,
  }, {
    skip: !entity?.value || !DATA_TYPES_WITH_OPTIONS.includes(entity?.dataType),
  });

  const ANY_ACTIVITY = [{ value: 'any', label: 'Any Activity' }];
  const ANY_TASK_TYPE = [{ value: 'any', label: 'Any Task' }];

  const optionsArr: any[] = [
    ...(entity?.entityType === FILTERABLE_ENTITY_TYPES.leadActivity ? ANY_ACTIVITY : []),
    ...(entity?.entityType === FILTERABLE_ENTITY_TYPES.taskType ? ANY_TASK_TYPE : []),
    ...(data?.[object] || []),
  ];

  const filteredOptions = entity?.filterVisibleConditionValues?.(optionsArr) || optionsArr;

  useEffect(() => {
    if (error) {
      dispatch(setFetchError('Failed To Fetch Data'));
    }
  }, [dispatch, error]);

  if (isLoading) {
    return <Select loading />;
  }

  return (
    <DataTypeSpecificInput
      entityType={entity?.entityType as FilterableEntityTypes}
      context={context}
      value={value}
      inputDataType={entity?.dataType}
      handleChange={handleChange}
      options={filteredOptions}
      comparisionType={comparisionType}
      dropdownProps={{
        remoteSearchable,
        searchValue: dropdownSearchString,
        onSearch: setDropdownSearchString,
      }}
    />
  );
}
